/**
 * Help Panel - Comprehensive game guide with navigation
 * A professionally designed help system with table of contents,
 * detailed gameplay instructions, and visual illustrations.
 */

export class HelpPanel {
  private container: HTMLElement;
  private visible: boolean = false;
  private currentSection: string = 'overview';

  constructor() {
    this.container = document.createElement('div');
    this.container.id = 'help-panel';
    this.container.innerHTML = this.buildHTML();
    document.body.appendChild(this.container);
    this.setupEvents();
  }

  private buildHTML(): string {
    return `
      <style>
        ${this.getStyles()}
      </style>
      <div class="help-overlay"></div>
      <div class="help-panel-wrapper">
        <div class="help-panel-corner tl"></div>
        <div class="help-panel-corner tr"></div>
        <div class="help-panel-corner bl"></div>
        <div class="help-panel-corner br"></div>

        <!-- Header -->
        <div class="help-panel-header">
          <div class="help-header-left">
            <div class="help-emblem"></div>
            <div class="help-title-group">
              <h1 class="help-main-title">天下归一</h1>
              <p class="help-subtitle">游戏指南与攻略大全</p>
            </div>
          </div>
          <button type="button" class="close-btn" aria-label="关闭">&times;</button>
        </div>

        <div class="help-body">
          <!-- Sidebar Navigation -->
          <nav class="help-sidebar">
            <div class="nav-section">
              <div class="nav-section-title">入门指南</div>
              <button class="nav-item active" data-section="overview">
                <span class="nav-icon">📖</span>游戏概述
              </button>
              <button class="nav-item" data-section="controls">
                <span class="nav-icon">🖱️</span>操作说明
              </button>
              <button class="nav-item" data-section="shortcuts">
                <span class="nav-icon">⌨️</span>快捷键
              </button>
            </div>

            <div class="nav-section">
              <div class="nav-section-title">势力与战争</div>
              <button class="nav-item" data-section="factions">
                <span class="nav-icon">⚔️</span>七国介绍
              </button>
              <button class="nav-item" data-section="units">
                <span class="nav-icon">🎖️</span>兵种图鉴
              </button>
              <button class="nav-item" data-section="buildings">
                <span class="nav-icon">🏛️</span>建筑大全
              </button>
            </div>

            <div class="nav-section">
              <div class="nav-section-title">进阶攻略</div>
              <button class="nav-item" data-section="terrain">
                <span class="nav-icon">🗺️</span>地形详解
              </button>
              <button class="nav-item" data-section="diplomacy">
                <span class="nav-icon">🤝</span>外交系统
              </button>
              <button class="nav-item" data-section="tech">
                <span class="nav-icon">📜</span>科技树
              </button>
              <button class="nav-item" data-section="strategy">
                <span class="nav-icon">💡</span>战术技巧
              </button>
            </div>
          </nav>

          <!-- Main Content Area -->
          <main class="help-content">
            ${this.buildAllSections()}
          </main>
        </div>

        <!-- Footer -->
        <div class="help-footer">
          <span class="footer-hint">按 <kbd>H</kbd> 随时打开此帮助</span>
          <span class="footer-version">版本 1.0</span>
        </div>
      </div>
    `;
  }

  private getStyles(): string {
    return `
      #help-panel {
        position: fixed;
        inset: 0;
        z-index: 260;
        font: 13px/1.6 'Noto Serif SC', 'SimSun', serif;
        color: #f5e6d3;
        display: none;
        align-items: center;
        justify-content: center;
      }
      #help-panel.visible {
        display: flex;
      }

      /* Overlay */
      .help-overlay {
        position: absolute;
        inset: 0;
        background: rgba(0, 0, 0, 0.75);
        backdrop-filter: blur(4px);
      }

      /* Main Wrapper */
      .help-panel-wrapper {
        position: relative;
        width: 90%;
        max-width: 1000px;
        height: 85vh;
        max-height: 750px;
        background:
          linear-gradient(145deg, rgba(74,55,40,0.98) 0%, rgba(35,24,16,0.99) 100%),
          url('/images/ui/elements/wood.webp');
        background-size: cover;
        border: 3px solid #8b6914;
        border-radius: 12px;
        box-shadow:
          inset 0 2px 8px rgba(255,220,150,0.12),
          inset 0 -4px 12px rgba(0,0,0,0.5),
          0 20px 60px rgba(0,0,0,0.8),
          0 0 0 1px rgba(212,175,55,0.2);
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }

      /* Corner Decorations - Using sprite sheet */
      .help-panel-corner {
        position: absolute;
        width: 64px;
        height: 64px;
        background-image: url('/images/ui/decorations/corner-ornaments.png');
        background-size: 200% 200%;
        z-index: 2;
        filter: drop-shadow(0 2px 4px rgba(0,0,0,0.5));
        pointer-events: none;
      }
      /* Sprite positions: TL(0,0) TR(1,0) BL(0,1) BR(1,1) */
      .help-panel-corner.tl {
        top: -8px;
        left: -8px;
        background-position: 0% 0%;
      }
      .help-panel-corner.tr {
        top: -8px;
        right: -8px;
        background-position: 100% 0%;
      }
      .help-panel-corner.bl {
        bottom: -8px;
        left: -8px;
        background-position: 0% 100%;
      }
      .help-panel-corner.br {
        bottom: -8px;
        right: -8px;
        background-position: 100% 100%;
      }

      /* Header */
      .help-panel-header {
        background: linear-gradient(180deg, #5c3d28 0%, #3d2617 100%);
        border-bottom: 2px solid #8b6914;
        padding: 16px 24px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-shrink: 0;
        position: relative;
        z-index: 5;
      }
      .help-panel-header::after {
        content: '';
        position: absolute;
        bottom: -42px;
        left: 50%;
        transform: translateX(-50%);
        width: 280px;
        height: 48px;
        background: url('/images/ui/decorations/title-bar.png') center/contain no-repeat;
        z-index: 1;
        pointer-events: none;
        opacity: 0.85;
      }
      .help-header-left {
        display: flex;
        align-items: center;
        gap: 16px;
      }
      .help-emblem {
        width: 48px;
        height: 48px;
        background: url('/images/bg.webp') center/cover;
        border: 2px solid #d4af37;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.5);
      }
      .help-title-group {
        display: flex;
        flex-direction: column;
      }
      .help-main-title {
        font: 700 24px/1.2 'Noto Serif SC', 'SimSun', serif;
        color: #ffd35a;
        text-shadow: 0 2px 8px rgba(0,0,0,0.6);
        margin: 0;
        letter-spacing: 4px;
      }
      .help-subtitle {
        color: #c4a77d;
        font-size: 12px;
        margin: 4px 0 0 0;
        letter-spacing: 1px;
      }
      #help-panel .close-btn {
        background: linear-gradient(180deg, #5c4033 0%, #3d2a1c 100%);
        border: 2px solid #8b6914;
        border-radius: 6px;
        color: #c4a77d;
        cursor: pointer;
        font-size: 24px;
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s;
        position: relative;
        z-index: 10;
      }
      #help-panel .close-btn:hover {
        background: linear-gradient(180deg, #6b4423 0%, #4a3020 100%);
        color: #ffd35a;
        border-color: #d4af37;
        box-shadow: 0 0 12px rgba(212,175,55,0.3);
      }
      #help-panel .close-btn:active {
        transform: scale(0.95);
      }

      /* Body Layout */
      .help-body {
        display: flex;
        flex: 1;
        overflow: hidden;
        min-height: 0;
      }

      /* Sidebar Navigation */
      .help-sidebar {
        width: 200px;
        background: linear-gradient(180deg, rgba(45,31,21,0.95) 0%, rgba(30,20,12,0.98) 100%);
        border-right: 2px solid rgba(139,105,20,0.5);
        padding: 16px 0;
        overflow-y: auto;
        flex-shrink: 0;
      }
      .help-sidebar::-webkit-scrollbar {
        width: 4px;
      }
      .help-sidebar::-webkit-scrollbar-thumb {
        background: #8b6914;
        border-radius: 2px;
      }
      .nav-section {
        margin-bottom: 20px;
      }
      .nav-section-title {
        font-size: 10px;
        font-weight: 700;
        color: #8b6914;
        text-transform: uppercase;
        letter-spacing: 2px;
        padding: 0 16px 8px;
        border-bottom: 1px solid rgba(139,105,20,0.3);
        margin-bottom: 8px;
      }
      .nav-item {
        display: flex;
        align-items: center;
        gap: 10px;
        width: 100%;
        padding: 10px 16px;
        background: transparent;
        border: none;
        color: #c4a77d;
        font: inherit;
        font-size: 13px;
        cursor: pointer;
        text-align: left;
        transition: all 0.15s;
        border-left: 3px solid transparent;
      }
      .nav-item:hover {
        background: rgba(212,175,55,0.08);
        color: #f5e6d3;
      }
      .nav-item.active {
        background: rgba(212,175,55,0.15);
        color: #ffd35a;
        border-left-color: #d4af37;
      }
      .nav-icon {
        font-size: 14px;
        width: 20px;
        text-align: center;
      }

      /* Main Content */
      .help-content {
        flex: 1;
        padding: 24px;
        overflow-y: auto;
      }
      .help-content::-webkit-scrollbar {
        width: 8px;
      }
      .help-content::-webkit-scrollbar-track {
        background: rgba(0,0,0,0.2);
        border-radius: 4px;
      }
      .help-content::-webkit-scrollbar-thumb {
        background: #8b6914;
        border-radius: 4px;
      }
      .help-content::-webkit-scrollbar-thumb:hover {
        background: #d4af37;
      }

      /* Content Sections */
      .content-section {
        display: none;
      }
      .content-section.active {
        display: block;
        animation: fadeIn 0.25s ease-out;
      }
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(8px); }
        to { opacity: 1; transform: translateY(0); }
      }

      /* Section Header */
      .section-header {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 28px;
        padding-bottom: 20px;
        position: relative;
      }
      .section-header::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 24px;
        background: url('/images/ui/decorations/divider.png') center/contain no-repeat;
        opacity: 0.7;
      }
      .section-header-icon {
        font-size: 28px;
      }
      .section-header h2 {
        font: 700 20px/1.2 'Noto Serif SC', serif;
        color: #ffd35a;
        margin: 0;
      }
      .section-header p {
        color: #9e8b6e;
        font-size: 12px;
        margin: 4px 0 0 0;
      }

      /* Content Cards */
      .info-card {
        background: linear-gradient(180deg, rgba(60,42,28,0.6) 0%, rgba(40,28,18,0.8) 100%);
        border: 1px solid rgba(139,105,20,0.4);
        border-radius: 8px;
        padding: 16px;
        margin-bottom: 16px;
      }
      .info-card-header {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 12px;
      }
      .info-card-title {
        font: 600 15px/1.2 'Noto Serif SC', serif;
        color: #ffd35a;
        margin: 0;
      }
      .info-card-desc {
        color: #c4a77d;
        font-size: 13px;
        line-height: 1.7;
      }

      /* Image Containers */
      .img-icon {
        width: 40px;
        height: 40px;
        border-radius: 6px;
        border: 2px solid #8b6914;
        object-fit: cover;
        background: rgba(0,0,0,0.3);
      }
      .img-icon-sm {
        width: 32px;
        height: 32px;
        border-radius: 4px;
        border: 1px solid #8b6914;
        object-fit: cover;
      }
      .img-icon-lg {
        width: 64px;
        height: 64px;
        border-radius: 8px;
        border: 2px solid #d4af37;
        object-fit: cover;
        box-shadow: 0 4px 12px rgba(0,0,0,0.4);
      }

      /* Grid Layouts */
      .grid-2 {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
      }
      .grid-3 {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 12px;
      }
      @media (max-width: 900px) {
        .grid-3 { grid-template-columns: repeat(2, 1fr); }
      }

      /* Unit/Building Cards */
      .entity-card {
        background: linear-gradient(180deg, rgba(50,35,22,0.8) 0%, rgba(35,24,15,0.9) 100%);
        border: 1px solid rgba(139,105,20,0.4);
        border-radius: 8px;
        padding: 12px;
        transition: all 0.2s;
      }
      .entity-card:hover {
        border-color: #d4af37;
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(0,0,0,0.4);
      }
      .entity-header {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 10px;
      }
      .entity-name {
        font: 600 14px/1.2 'Noto Serif SC', serif;
        color: #ffd35a;
      }
      .entity-tag {
        font-size: 10px;
        padding: 2px 6px;
        border-radius: 3px;
        background: rgba(212,175,55,0.2);
        color: #d4af37;
        margin-left: auto;
      }
      .entity-stats {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 6px;
        margin-bottom: 8px;
      }
      .stat-item {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 11px;
        color: #9e8b6e;
      }
      .stat-value {
        color: #f5e6d3;
        font-weight: 600;
      }
      .entity-desc {
        font-size: 11px;
        color: #9e8b6e;
        line-height: 1.5;
        border-top: 1px solid rgba(139,105,20,0.3);
        padding-top: 8px;
        margin-top: 4px;
      }

      /* Faction Cards */
      .faction-card {
        background: linear-gradient(180deg, rgba(50,35,22,0.9) 0%, rgba(30,20,12,0.95) 100%);
        border: 2px solid rgba(139,105,20,0.5);
        border-radius: 10px;
        overflow: hidden;
        transition: all 0.2s;
      }
      .faction-card:hover {
        border-color: var(--faction-color, #d4af37);
        transform: translateY(-2px);
        box-shadow: 0 8px 24px rgba(0,0,0,0.5);
      }
      .faction-banner {
        height: 60px;
        background-size: cover;
        background-position: center;
        position: relative;
      }
      .faction-banner::after {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(to bottom, transparent 0%, rgba(30,20,12,0.9) 100%);
      }
      .faction-emblem {
        position: absolute;
        bottom: -20px;
        left: 12px;
        width: 44px;
        height: 44px;
        border-radius: 8px;
        border: 2px solid var(--faction-color, #d4af37);
        z-index: 1;
        object-fit: cover;
        box-shadow: 0 4px 12px rgba(0,0,0,0.5);
      }
      .faction-body {
        padding: 28px 12px 12px;
      }
      .faction-name {
        font: 700 16px/1.2 'Noto Serif SC', serif;
        color: var(--faction-color, #ffd35a);
        margin-bottom: 4px;
      }
      .faction-trait {
        font-size: 11px;
        color: #d4af37;
        margin-bottom: 8px;
      }
      .faction-desc {
        font-size: 11px;
        color: #9e8b6e;
        line-height: 1.5;
      }

      /* Keyboard Grid */
      .key-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 10px;
      }
      .key-item {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 8px 12px;
        background: rgba(0,0,0,0.2);
        border-radius: 6px;
        border: 1px solid rgba(139,105,20,0.3);
      }
      #help-panel kbd {
        background: linear-gradient(180deg, #5c4033 0%, #3d2a1c 100%);
        padding: 5px 12px;
        border-radius: 4px;
        border: 1px solid #8b6914;
        font-family: inherit;
        color: #ffd35a;
        font-size: 12px;
        min-width: 45px;
        text-align: center;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,220,150,0.1);
      }
      .key-desc {
        color: #c4a77d;
        font-size: 12px;
      }

      /* Terrain Cards */
      .terrain-card {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 12px;
        background: rgba(0,0,0,0.25);
        border-radius: 8px;
        border: 1px solid rgba(139,105,20,0.3);
      }
      .terrain-preview {
        width: 48px;
        height: 48px;
        border-radius: 6px;
        flex-shrink: 0;
      }
      .terrain-info h4 {
        font: 600 13px/1.2 'Noto Serif SC', serif;
        color: #ffd35a;
        margin: 0 0 4px 0;
      }
      .terrain-info p {
        font-size: 11px;
        color: #9e8b6e;
        margin: 0;
        line-height: 1.5;
      }
      .terrain-effects {
        display: flex;
        gap: 8px;
        margin-top: 6px;
        flex-wrap: wrap;
      }
      .terrain-effect {
        font-size: 10px;
        padding: 2px 6px;
        border-radius: 3px;
        background: rgba(0,0,0,0.3);
      }
      .effect-positive { color: #4caf50; }
      .effect-negative { color: #ef5350; }
      .effect-neutral { color: #c4a77d; }

      /* Tips Box */
      .tip-box {
        background: linear-gradient(135deg, rgba(212,175,55,0.1) 0%, rgba(139,105,20,0.1) 100%);
        border: 1px solid rgba(212,175,55,0.3);
        border-radius: 8px;
        padding: 16px;
        margin-top: 16px;
      }
      .tip-header {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 10px;
      }
      .tip-header span {
        font: 600 14px/1 'Noto Serif SC', serif;
        color: #ffd35a;
      }
      .tip-list {
        list-style: none;
        padding: 0;
        margin: 0;
      }
      .tip-list li {
        position: relative;
        padding-left: 18px;
        color: #c4a77d;
        font-size: 12px;
        line-height: 1.8;
      }
      .tip-list li::before {
        content: '◆';
        position: absolute;
        left: 0;
        color: #d4af37;
        font-size: 8px;
        top: 6px;
      }

      /* Counter Relationship Diagram */
      .counter-diagram {
        background: rgba(0,0,0,0.3);
        border-radius: 8px;
        padding: 16px;
        margin: 16px 0;
      }
      .counter-row {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        margin: 8px 0;
      }
      .counter-unit {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 4px;
      }
      .counter-arrow {
        color: #4caf50;
        font-size: 18px;
      }
      .counter-label {
        font-size: 11px;
        color: #c4a77d;
      }

      /* Divider - Using decorative image */
      .section-divider {
        height: 32px;
        background: url('/images/ui/decorations/divider.png') center/contain no-repeat;
        margin: 24px 0;
        opacity: 0.85;
      }

      /* Decorative Button - Using sprite sheet (4 states: normal, hover, pressed, disabled) */
      .deco-btn {
        position: relative;
        padding: 10px 24px;
        background: url('/images/ui/decorations/button-states.png') 0% 50% / 400% auto no-repeat;
        border: none;
        color: #f5e6d3;
        font: 600 13px/1 'Noto Serif SC', serif;
        cursor: pointer;
        transition: background-position 0.15s;
        min-width: 100px;
        text-shadow: 0 1px 2px rgba(0,0,0,0.5);
      }
      .deco-btn:hover {
        background-position: 33.33% 50%;
        color: #ffd35a;
      }
      .deco-btn:active {
        background-position: 66.66% 50%;
      }
      .deco-btn:disabled {
        background-position: 100% 50%;
        color: #9e8b6e;
        cursor: not-allowed;
      }

      /* Footer */
      .help-footer {
        background: linear-gradient(180deg, rgba(45,31,21,0.98) 0%, rgba(30,20,12,1) 100%);
        border-top: 2px solid rgba(139,105,20,0.5);
        padding: 12px 24px;
        display: flex;
        justify-content: space-between;
        flex-shrink: 0;
        position: relative;
        z-index: 5;
        align-items: center;
        flex-shrink: 0;
      }
      .footer-hint {
        color: #9e8b6e;
        font-size: 11px;
      }
      .footer-hint kbd {
        margin: 0 4px;
        padding: 2px 6px;
        font-size: 10px;
      }
      .footer-version {
        color: #6b5a45;
        font-size: 10px;
      }
    `;
  }

  private buildAllSections(): string {
    return `
      ${this.buildOverviewSection()}
      ${this.buildControlsSection()}
      ${this.buildShortcutsSection()}
      ${this.buildFactionsSection()}
      ${this.buildUnitsSection()}
      ${this.buildBuildingsSection()}
      ${this.buildTerrainSection()}
      ${this.buildDiplomacySection()}
      ${this.buildTechSection()}
      ${this.buildStrategySection()}
    `;
  }

  private buildOverviewSection(): string {
    return `
      <section class="content-section active" data-section="overview">
        <div class="section-header">
          <span class="section-header-icon">📖</span>
          <div>
            <h2>游戏概述</h2>
            <p>欢迎来到七国争霸的乱世</p>
          </div>
        </div>

        <div class="info-card">
          <div class="info-card-header">
            <h3 class="info-card-title">游戏背景</h3>
          </div>
          <div class="info-card-desc">
            《天下归一》是一款以中国古代七国争霸为背景的即时策略游戏。在这片广袤的大陆上，
            七个各具特色的国家分别占据一方：炎国的烈焰、寒国的冰雪、林国的森林、商国的财富、
            玄国的神秘、铁国的坚固、云国的疾风。你将选择其中一国，通过军事征伐、外交谋略、
            科技发展，最终统一天下，成为唯一的霸主。
          </div>
        </div>

        <div class="info-card">
          <div class="info-card-header">
            <h3 class="info-card-title">游戏目标</h3>
          </div>
          <div class="info-card-desc">
            <strong>标准战役</strong>：征服所有敌方王城，统一七国。<br>
            <strong>速战模式</strong>：在限定时间内击败对手。<br>
            <strong>无尽模式</strong>：抵御无穷无尽的敌军波次，生存越久得分越高。
          </div>
        </div>

        <div class="grid-2">
          <div class="info-card">
            <div class="info-card-header">
              <img src="/images/ui/icons/gold.webp" alt="金币" class="img-icon-sm">
              <h3 class="info-card-title">资源管理</h3>
            </div>
            <div class="info-card-desc">
              金币是唯一的资源，用于建造建筑、训练军队。占领更多领地、建造经济建筑可增加收入。
            </div>
          </div>
          <div class="info-card">
            <div class="info-card-header">
              <img src="/images/ui/icons/territory.webp" alt="领地" class="img-icon-sm">
              <h3 class="info-card-title">领地扩张</h3>
            </div>
            <div class="info-card-desc">
              建造建筑可扩张领地范围。占领敌方建筑可削弱敌人并增强自己。保护好你的王城！
            </div>
          </div>
        </div>

        <div class="tip-box">
          <div class="tip-header">
            <span>💡</span>
            <span>新手建议</span>
          </div>
          <ul class="tip-list">
            <li>游戏开始后，先建造农场和矿场增加收入</li>
            <li>训练一定数量的军队保护领土</li>
            <li>善用游戏暂停功能（空格键）进行战术规划</li>
            <li>注意观察敌方动向，适时调整战略</li>
          </ul>
        </div>
      </section>
    `;
  }

  private buildControlsSection(): string {
    return `
      <section class="content-section" data-section="controls">
        <div class="section-header">
          <span class="section-header-icon">🖱️</span>
          <div>
            <h2>操作说明</h2>
            <p>掌握基础操作，指挥千军万马</p>
          </div>
        </div>

        <div class="info-card">
          <div class="info-card-header">
            <h3 class="info-card-title">鼠标操作</h3>
          </div>
          <div class="info-card-desc">
            <table style="width:100%; border-collapse: collapse;">
              <tr style="border-bottom: 1px solid rgba(139,105,20,0.3);">
                <td style="padding: 8px 0; color: #ffd35a; width: 120px;">左键点击</td>
                <td style="padding: 8px 0;">选择单个单位或建筑</td>
              </tr>
              <tr style="border-bottom: 1px solid rgba(139,105,20,0.3);">
                <td style="padding: 8px 0; color: #ffd35a;">左键拖动</td>
                <td style="padding: 8px 0;">框选多个单位</td>
              </tr>
              <tr style="border-bottom: 1px solid rgba(139,105,20,0.3);">
                <td style="padding: 8px 0; color: #ffd35a;">右键点击</td>
                <td style="padding: 8px 0;">移动选中单位 / 攻击敌方目标</td>
              </tr>
              <tr style="border-bottom: 1px solid rgba(139,105,20,0.3);">
                <td style="padding: 8px 0; color: #ffd35a;">滚轮滚动</td>
                <td style="padding: 8px 0;">缩放视角</td>
              </tr>
              <tr>
                <td style="padding: 8px 0; color: #ffd35a;">中键拖动</td>
                <td style="padding: 8px 0;">平移视角</td>
              </tr>
            </table>
          </div>
        </div>

        <div class="info-card">
          <div class="info-card-header">
            <h3 class="info-card-title">编队系统</h3>
          </div>
          <div class="info-card-desc">
            选中单位后，按 <kbd>Ctrl</kbd> + <kbd>1~9</kbd> 创建编队。<br>
            之后按对应数字键即可快速选中该编队。<br>
            双击数字键可将视角移动到编队位置。
          </div>
        </div>

        <div class="info-card">
          <div class="info-card-header">
            <h3 class="info-card-title">建造操作</h3>
          </div>
          <div class="info-card-desc">
            1. 在屏幕下方的建造菜单中选择建筑<br>
            2. 在地图上合适的位置点击放置<br>
            3. 按 <kbd>Esc</kbd> 取消建造<br>
            4. 选中建筑后按 <kbd>Delete</kbd> 可拆除建筑
          </div>
        </div>

        <div class="tip-box">
          <div class="tip-header">
            <span>💡</span>
            <span>操作技巧</span>
          </div>
          <ul class="tip-list">
            <li>按住 Shift 可追加命令队列，让单位依次执行多个指令</li>
            <li>双击某个单位可选中屏幕内所有同类型单位</li>
            <li>点击小地图可快速移动视角</li>
          </ul>
        </div>
      </section>
    `;
  }

  private buildShortcutsSection(): string {
    return `
      <section class="content-section" data-section="shortcuts">
        <div class="section-header">
          <span class="section-header-icon">⌨️</span>
          <div>
            <h2>快捷键</h2>
            <p>熟练使用快捷键，提高操作效率</p>
          </div>
        </div>

        <div class="info-card">
          <div class="info-card-header">
            <h3 class="info-card-title">游戏控制</h3>
          </div>
          <div class="key-grid">
            <div class="key-item"><kbd>空格</kbd><span class="key-desc">暂停 / 继续游戏</span></div>
            <div class="key-item"><kbd>Esc</kbd><span class="key-desc">打开游戏菜单</span></div>
            <div class="key-item"><kbd>+</kbd><span class="key-desc">加快游戏速度</span></div>
            <div class="key-item"><kbd>-</kbd><span class="key-desc">减慢游戏速度</span></div>
          </div>
        </div>

        <div class="info-card">
          <div class="info-card-header">
            <h3 class="info-card-title">面板开关</h3>
          </div>
          <div class="key-grid">
            <div class="key-item"><kbd>H</kbd><span class="key-desc">帮助面板</span></div>
            <div class="key-item"><kbd>F</kbd><span class="key-desc">势力面板</span></div>
            <div class="key-item"><kbd>R</kbd><span class="key-desc">外交面板</span></div>
            <div class="key-item"><kbd>T</kbd><span class="key-desc">科技面板</span></div>
            <div class="key-item"><kbd>O</kbd><span class="key-desc">设置面板</span></div>
            <div class="key-item"><kbd>V</kbd><span class="key-desc">切换战争迷雾</span></div>
          </div>
        </div>

        <div class="info-card">
          <div class="info-card-header">
            <h3 class="info-card-title">存档与读档</h3>
          </div>
          <div class="key-grid">
            <div class="key-item"><kbd>F5</kbd><span class="key-desc">快速存档</span></div>
            <div class="key-item"><kbd>F9</kbd><span class="key-desc">快速读档</span></div>
          </div>
        </div>

        <div class="info-card">
          <div class="info-card-header">
            <h3 class="info-card-title">编队操作</h3>
          </div>
          <div class="key-grid">
            <div class="key-item"><kbd>1~9</kbd><span class="key-desc">选择编队</span></div>
            <div class="key-item"><kbd>Ctrl+1~9</kbd><span class="key-desc">创建/覆盖编队</span></div>
          </div>
        </div>

        <div class="info-card">
          <div class="info-card-header">
            <h3 class="info-card-title">建筑操作</h3>
          </div>
          <div class="key-grid">
            <div class="key-item"><kbd>Delete</kbd><span class="key-desc">拆除选中建筑</span></div>
          </div>
        </div>
      </section>
    `;
  }

  private buildFactionsSection(): string {
    const factions = [
      { id: 'yan', name: '炎国', color: '#c73e3a', trait: '攻击 +15%', terrain: '火山', desc: '烈焰之国，崇尚武力，战士凶猛无比，在火山地带如鱼得水。' },
      { id: 'han', name: '寒国', color: '#5b9bd5', trait: '防御 +20%', terrain: '雪原', desc: '冰雪之国，坚韧顽强，军队防御力超群，寒冬是他们的主场。' },
      { id: 'lin', name: '林国', color: '#4caf50', trait: '均衡派系', terrain: '森林', desc: '林海之国，与自然和谐共生，军队均衡全面，擅长森林作战。' },
      { id: 'shang', name: '商国', color: '#ffd700', trait: '收入 +40%', terrain: '荒漠', desc: '商贸之国，富甲天下，金币收入远超他国，善于以财制敌。' },
      { id: 'xuan', name: '玄国', color: '#9c27b0', trait: '道士加成', terrain: '沼泽', desc: '神秘之国，道法高深，道士力量强大，沼泽是他们的领域。' },
      { id: 'tie', name: '铁国', color: '#607d8b', trait: '建筑 +30%', terrain: '山丘', desc: '钢铁之国，城防坚固，建筑耐久度极高，山地是天然屏障。' },
      { id: 'yun', name: '云国', color: '#03a9f4', trait: '移速 +35%', terrain: '草原', desc: '疾风之国，来去如风，骑兵移动速度极快，草原上纵横驰骋。' },
    ];

    const factionCards = factions.map(f => `
      <div class="faction-card" style="--faction-color: ${f.color}">
        <div class="faction-banner" style="background-image: url('/images/factions/${f.id}.webp')"></div>
        <img src="/images/emblems/${f.id}.webp" alt="${f.name}" class="faction-emblem">
        <div class="faction-body">
          <div class="faction-name">${f.name}</div>
          <div class="faction-trait">${f.trait} · ${f.terrain}特化</div>
          <div class="faction-desc">${f.desc}</div>
        </div>
      </div>
    `).join('');

    return `
      <section class="content-section" data-section="factions">
        <div class="section-header">
          <span class="section-header-icon">⚔️</span>
          <div>
            <h2>七国介绍</h2>
            <p>了解各国特色，选择你的归属</p>
          </div>
        </div>

        <div class="info-card">
          <div class="info-card-desc">
            每个国家都有独特的种族特性和地形加成。选择适合你战略风格的国家，
            发挥其长处，规避其短板，是走向胜利的第一步。
          </div>
        </div>

        <div class="grid-2" style="margin-top: 16px;">
          ${factionCards}
        </div>

        <div class="tip-box">
          <div class="tip-header">
            <span>💡</span>
            <span>选国建议</span>
          </div>
          <ul class="tip-list">
            <li><strong>新手推荐</strong>：林国（均衡）或寒国（高防御容错高）</li>
            <li><strong>进攻流</strong>：炎国（高攻击）或云国（高机动）</li>
            <li><strong>经济流</strong>：商国（高收入可暴兵）</li>
            <li><strong>防守流</strong>：铁国（坚固建筑）或寒国（高防御）</li>
          </ul>
        </div>
      </section>
    `;
  }

  private buildUnitsSection(): string {
    const units = [
      { id: 'sword', name: '剑士', hp: 100, atk: 15, def: 5, spd: '1.0x', range: '1.5', cost: 50, desc: '近战步兵，攻守均衡的基础单位。成本低廉，适合前期大量生产。', tag: '步兵' },
      { id: 'archer', name: '弓箭手', hp: 60, atk: 20, def: 2, spd: '0.9x', range: '5.0', cost: 80, desc: '远程单位，可在安全距离输出伤害。克制步兵，但怕骑兵突脸。', tag: '远程' },
      { id: 'spear', name: '枪兵', hp: 120, atk: 25, def: 8, spd: '0.8x', range: '2.0', cost: 100, desc: '长枪步兵，专门克制骑兵。防御较高，是抵挡骑兵冲锋的最佳选择。', tag: '步兵' },
      { id: 'cavalry', name: '骑兵', hp: 150, atk: 30, def: 6, spd: '2.0x', range: '1.5', cost: 200, desc: '高机动单位，适合快速突击和包抄。克制弓箭手，但怕枪兵。', tag: '骑兵' },
      { id: 'shield', name: '盾兵', hp: 200, atk: 8, def: 15, spd: '0.6x', range: '1.5', cost: 120, desc: '重装步兵，超高防御力。能有效抵挡远程攻击，适合保护后排。', tag: '步兵' },
      { id: 'taoist', name: '道士', hp: 50, atk: 40, def: 1, spd: '1.0x', range: '4.0', cost: 150, desc: '法术单位，释放范围伤害。攻击力极高但非常脆弱，需要保护。', tag: '法师' },
    ];

    const unitCards = units.map(u => `
      <div class="entity-card">
        <div class="entity-header">
          <img src="/images/ui/units/${u.id}.webp" alt="${u.name}" class="img-icon">
          <span class="entity-name">${u.name}</span>
          <span class="entity-tag">${u.tag}</span>
        </div>
        <div class="entity-stats">
          <div class="stat-item">❤️ 生命 <span class="stat-value">${u.hp}</span></div>
          <div class="stat-item">⚔️ 攻击 <span class="stat-value">${u.atk}</span></div>
          <div class="stat-item">🛡️ 防御 <span class="stat-value">${u.def}</span></div>
          <div class="stat-item">👟 速度 <span class="stat-value">${u.spd}</span></div>
          <div class="stat-item">🎯 射程 <span class="stat-value">${u.range}</span></div>
          <div class="stat-item">💰 成本 <span class="stat-value">${u.cost}g</span></div>
        </div>
        <div class="entity-desc">${u.desc}</div>
      </div>
    `).join('');

    return `
      <section class="content-section" data-section="units">
        <div class="section-header">
          <span class="section-header-icon">🎖️</span>
          <div>
            <h2>兵种图鉴</h2>
            <p>知己知彼，百战不殆</p>
          </div>
        </div>

        <div class="grid-2">
          ${unitCards}
        </div>

        <div class="section-divider"></div>

        <div class="info-card">
          <div class="info-card-header">
            <h3 class="info-card-title">兵种克制关系</h3>
          </div>
          <div class="counter-diagram">
            <div class="counter-row">
              <div class="counter-unit">
                <img src="/images/ui/units/archer.webp" alt="弓箭手" class="img-icon-sm">
                <span class="counter-label">弓箭手</span>
              </div>
              <span class="counter-arrow">→ 克制 →</span>
              <div class="counter-unit">
                <img src="/images/ui/units/sword.webp" alt="剑士" class="img-icon-sm">
                <span class="counter-label">步兵</span>
              </div>
            </div>
            <div class="counter-row">
              <div class="counter-unit">
                <img src="/images/ui/units/cavalry.webp" alt="骑兵" class="img-icon-sm">
                <span class="counter-label">骑兵</span>
              </div>
              <span class="counter-arrow">→ 克制 →</span>
              <div class="counter-unit">
                <img src="/images/ui/units/archer.webp" alt="弓箭手" class="img-icon-sm">
                <span class="counter-label">弓箭手</span>
              </div>
            </div>
            <div class="counter-row">
              <div class="counter-unit">
                <img src="/images/ui/units/spear.webp" alt="枪兵" class="img-icon-sm">
                <span class="counter-label">枪兵</span>
              </div>
              <span class="counter-arrow">→ 克制 →</span>
              <div class="counter-unit">
                <img src="/images/ui/units/cavalry.webp" alt="骑兵" class="img-icon-sm">
                <span class="counter-label">骑兵</span>
              </div>
            </div>
            <div class="counter-row">
              <div class="counter-unit">
                <img src="/images/ui/units/shield.webp" alt="盾兵" class="img-icon-sm">
                <span class="counter-label">盾兵</span>
              </div>
              <span class="counter-arrow">→ 抵挡 →</span>
              <div class="counter-unit">
                <img src="/images/ui/units/archer.webp" alt="弓箭手" class="img-icon-sm">
                <span class="counter-label">远程</span>
              </div>
            </div>
          </div>
        </div>

        <div class="tip-box">
          <div class="tip-header">
            <span>💡</span>
            <span>配兵建议</span>
          </div>
          <ul class="tip-list">
            <li>前期以剑士为主力，经济稳定后加入弓箭手提高输出</li>
            <li>遭遇敌方骑兵时，优先补充枪兵进行反制</li>
            <li>道士伤害极高但血量脆弱，需要盾兵或步兵保护</li>
            <li>骑兵适合绕后偷袭敌方弓箭手和道士</li>
          </ul>
        </div>
      </section>
    `;
  }

  private buildBuildingsSection(): string {
    const buildings = [
      { id: 'farm', name: '农场', cost: 100, income: '+30g/分', type: '经济', desc: '基础经济建筑，提供稳定的金币收入。' },
      { id: 'mine', name: '矿场', cost: 150, income: '+50g/分', type: '经济', desc: '高级经济建筑，收入比农场更高。' },
      { id: 'market', name: '市场', cost: 200, income: '收入+20%', type: '经济', desc: '提升所有经济建筑的收入效率。' },
      { id: 'barracks', name: '兵营', cost: 150, income: '训练剑士', type: '军事', desc: '基础军事建筑，可训练剑士单位。' },
      { id: 'archery', name: '射箭场', cost: 200, income: '训练弓兵', type: '军事', desc: '远程单位训练营，可训练弓箭手。' },
      { id: 'spearcamp', name: '枪营', cost: 200, income: '训练枪兵', type: '军事', desc: '专门训练枪兵，克制敌方骑兵。' },
      { id: 'stable', name: '马厩', cost: 300, income: '训练骑兵', type: '军事', desc: '骑兵训练营，可训练高机动骑兵。' },
      { id: 'smithy', name: '铁匠铺', cost: 250, income: '训练盾兵', type: '军事', desc: '重装步兵训练营，可训练盾兵。' },
      { id: 'temple', name: '神庙', cost: 350, income: '训练道士', type: '军事', desc: '道法训练圣地，可训练道士单位。' },
      { id: 'tower', name: '箭塔', cost: 200, income: '远程防御', type: '防御', desc: '防御建筑，自动攻击范围内敌人。' },
    ];

    const buildingCards = buildings.map(b => `
      <div class="entity-card">
        <div class="entity-header">
          <img src="/images/ui/buildings/${b.id}.webp" alt="${b.name}" class="img-icon">
          <span class="entity-name">${b.name}</span>
          <span class="entity-tag">${b.type}</span>
        </div>
        <div class="entity-stats">
          <div class="stat-item">💰 成本 <span class="stat-value">${b.cost}g</span></div>
          <div class="stat-item">📊 效果 <span class="stat-value">${b.income}</span></div>
        </div>
        <div class="entity-desc">${b.desc}</div>
      </div>
    `).join('');

    return `
      <section class="content-section" data-section="buildings">
        <div class="section-header">
          <span class="section-header-icon">🏛️</span>
          <div>
            <h2>建筑大全</h2>
            <p>稳固根基，发展国力</p>
          </div>
        </div>

        <div class="info-card">
          <div class="info-card-desc">
            建筑是发展国力的基石。合理规划经济建筑保证收入，军事建筑提供兵源，
            防御建筑保护领地。每座建筑都会扩张你的领土范围。
          </div>
        </div>

        <div class="grid-2" style="margin-top: 16px;">
          ${buildingCards}
        </div>

        <div class="section-divider"></div>

        <div class="info-card">
          <div class="info-card-header">
            <h3 class="info-card-title">特殊建筑</h3>
          </div>
          <div class="info-card-desc">
            <strong>🏯 王城</strong>：每个国家的核心建筑，一旦被摧毁则该国灭亡。保护好自己的王城！<br><br>
            <strong>🧱 城墙</strong>：防御工事，可阻挡敌军移动。相邻的城墙会自动连接形成防线。成本 80g。
          </div>
        </div>

        <div class="tip-box">
          <div class="tip-header">
            <span>💡</span>
            <span>建设建议</span>
          </div>
          <ul class="tip-list">
            <li>游戏初期优先建造2-3个农场确保经济基础</li>
            <li>在边境建造箭塔可以有效预警和阻挡敌军</li>
            <li>军营类建筑靠近前线可以快速补充兵力</li>
            <li>在关键要道建造城墙可以大大增加防御能力</li>
          </ul>
        </div>
      </section>
    `;
  }

  private buildTerrainSection(): string {
    const terrains = [
      { name: '平原', color: '#8fbc8f', effects: [], desc: '标准地形，无特殊效果。' },
      { name: '草原', color: '#9acd32', effects: [{ text: '移速+10%', type: 'positive' }], desc: '开阔平坦，有利于骑兵机动。商国单位获得额外加成。' },
      { name: '森林', color: '#228b22', effects: [{ text: '移速-30%', type: 'negative' }, { text: '防御+15%', type: 'positive' }], desc: '茂密树林阻碍行军，但利于防守。林国单位免疫减速。' },
      { name: '山丘', color: '#8b7355', effects: [{ text: '移速-20%', type: 'negative' }, { text: '防御+25%', type: 'positive' }], desc: '崎岖地形难以行军，但居高临下防御优势大。铁国单位获得额外加成。' },
      { name: '雪原', color: '#b0c4de', effects: [{ text: '移速-40%', type: 'negative' }, { text: '防御+10%', type: 'positive' }], desc: '冰天雪地严重阻碍行军。寒国单位免疫所有减益效果。' },
      { name: '火山', color: '#dc143c', effects: [{ text: '移速-30%', type: 'negative' }, { text: '攻击+20%', type: 'positive' }], desc: '炙热地带激发战斗潜能。炎国单位免疫减速并获得额外攻击加成。' },
      { name: '沼泽', color: '#556b2f', effects: [{ text: '移速-50%', type: 'negative' }, { text: '攻击-10%', type: 'negative' }, { text: '防御+10%', type: 'positive' }], desc: '泥泞难行，极大削弱军队战斗力。玄国单位免疫所有减益效果。' },
      { name: '荒漠', color: '#deb887', effects: [{ text: '移速-15%', type: 'negative' }], desc: '干旱贫瘠之地。云国单位免疫减速效果。' },
    ];

    const terrainCards = terrains.map(t => `
      <div class="terrain-card">
        <div class="terrain-preview" style="background: ${t.color};"></div>
        <div class="terrain-info">
          <h4>${t.name}</h4>
          <p>${t.desc}</p>
          <div class="terrain-effects">
            ${t.effects.map(e => `<span class="terrain-effect effect-${e.type}">${e.text}</span>`).join('')}
          </div>
        </div>
      </div>
    `).join('');

    return `
      <section class="content-section" data-section="terrain">
        <div class="section-header">
          <span class="section-header-icon">🗺️</span>
          <div>
            <h2>地形详解</h2>
            <p>善用地利，扭转战局</p>
          </div>
        </div>

        <div class="info-card">
          <div class="info-card-desc">
            地形对作战有重大影响。了解各种地形的效果，选择有利地形作战，
            避免在不利地形与敌人交战，是取胜的关键。
          </div>
        </div>

        <div class="grid-2" style="margin-top: 16px;">
          ${terrainCards}
        </div>

        <div class="tip-box">
          <div class="tip-header">
            <span>💡</span>
            <span>地形战术</span>
          </div>
          <ul class="tip-list">
            <li>利用己方的特色地形作战可获得巨大优势</li>
            <li>避免在敌方特色地形与其正面交锋</li>
            <li>山丘和森林是防守的绝佳位置</li>
            <li>草原利于骑兵作战，可用于快速突击</li>
          </ul>
        </div>
      </section>
    `;
  }

  private buildDiplomacySection(): string {
    return `
      <section class="content-section" data-section="diplomacy">
        <div class="section-header">
          <span class="section-header-icon">🤝</span>
          <div>
            <h2>外交系统</h2>
            <p>远交近攻，纵横捭阖</p>
          </div>
        </div>

        <div class="info-card">
          <div class="info-card-header">
            <h3 class="info-card-title">外交关系</h3>
          </div>
          <div class="info-card-desc">
            与其他国家的关系值范围为 -100（敌对）到 +100（友好）。<br>
            关系值会影响AI的行为倾向和外交选项。
          </div>
        </div>

        <div class="grid-2">
          <div class="info-card">
            <div class="info-card-header">
              <h3 class="info-card-title">⚔️ 宣战</h3>
            </div>
            <div class="info-card-desc">
              向目标国家宣战，进入战争状态。可攻击其单位和建筑。注意：宣战会大幅降低与其他国家的关系。
            </div>
          </div>
          <div class="info-card">
            <div class="info-card-header">
              <h3 class="info-card-title">🕊️ 求和</h3>
            </div>
            <div class="info-card-desc">
              向交战国提出停战。需要对方同意。若优势方提出求和更容易被接受。
            </div>
          </div>
          <div class="info-card">
            <div class="info-card-header">
              <h3 class="info-card-title">📜 结盟</h3>
            </div>
            <div class="info-card-desc">
              与目标国家建立同盟。同盟国不会互相攻击，且可能在战争中互相支援。
            </div>
          </div>
          <div class="info-card">
            <div class="info-card-header">
              <h3 class="info-card-title">💰 贸易</h3>
            </div>
            <div class="info-card-desc">
              与目标国家建立贸易关系，双方都获得少量经济加成，并提升关系值。
            </div>
          </div>
        </div>

        <div class="section-divider"></div>

        <div class="info-card">
          <div class="info-card-header">
            <h3 class="info-card-title">历史恩怨</h3>
          </div>
          <div class="info-card-desc">
            游戏开始时，部分国家之间存在历史关系：<br><br>
            <span style="color: #ef5350;">◆ 炎国与寒国</span> - 世仇敌对（-80）<br>
            <span style="color: #ef5350;">◆ 林国与铁国</span> - 边境纷争（-40）<br>
            <span style="color: #4caf50;">◆ 商国与玄国</span> - 贸易伙伴（+60）<br>
          </div>
        </div>

        <div class="tip-box">
          <div class="tip-header">
            <span>💡</span>
            <span>外交策略</span>
          </div>
          <ul class="tip-list">
            <li>避免同时与多个国家交战，容易被围攻</li>
            <li>远交近攻：与远方国家结盟，集中攻击邻国</li>
            <li>坐山观虎斗：让敌人互相消耗后再出击</li>
            <li>通过贸易慢慢改善与邻国的关系可避免突然袭击</li>
          </ul>
        </div>
      </section>
    `;
  }

  private buildTechSection(): string {
    return `
      <section class="content-section" data-section="tech">
        <div class="section-header">
          <span class="section-header-icon">📜</span>
          <div>
            <h2>科技树</h2>
            <p>研发科技，提升国力</p>
          </div>
        </div>

        <div class="info-card">
          <div class="info-card-desc">
            科技研发可以全面提升国家实力。按 <kbd>T</kbd> 打开科技面板，
            花费金币研发各种科技。科技分为军事、经济、建筑三大类。
          </div>
        </div>

        <div class="grid-3">
          <div class="info-card">
            <div class="info-card-header">
              <h3 class="info-card-title">⚔️ 军事科技</h3>
            </div>
            <div class="info-card-desc" style="font-size: 12px;">
              <strong>锐兵之术</strong><br>攻击力 +10%<br><br>
              <strong>铁壁之术</strong><br>防御力 +10%<br><br>
              <strong>疾行之术</strong><br>移动速度 +15%<br><br>
              <strong>精准射击</strong><br>弓箭手伤害 +20%
            </div>
          </div>
          <div class="info-card">
            <div class="info-card-header">
              <h3 class="info-card-title">💰 经济科技</h3>
            </div>
            <div class="info-card-desc" style="font-size: 12px;">
              <strong>农耕改良</strong><br>农场收入 +30%<br><br>
              <strong>矿脉探索</strong><br>矿场收入 +30%<br><br>
              <strong>商贸繁荣</strong><br>市场加成 +50%<br><br>
              <strong>税收改革</strong><br>整体收入 +20%
            </div>
          </div>
          <div class="info-card">
            <div class="info-card-header">
              <h3 class="info-card-title">🏛️ 建筑科技</h3>
            </div>
            <div class="info-card-desc" style="font-size: 12px;">
              <strong>坚固城防</strong><br>建筑耐久 +30%<br><br>
              <strong>快速建造</strong><br>建造速度 +25%<br><br>
              <strong>节省成本</strong><br>建筑成本 -15%<br><br>
              <strong>延伸领地</strong><br>领地范围 +20%
            </div>
          </div>
        </div>

        <div class="tip-box">
          <div class="tip-header">
            <span>💡</span>
            <span>科技优先级</span>
          </div>
          <ul class="tip-list">
            <li>开局优先研发经济科技，增加收入滚雪球</li>
            <li>准备进攻前研发军事科技提升战斗力</li>
            <li>防守方优先考虑建筑科技增强城防</li>
            <li>根据战局灵活调整科技路线</li>
          </ul>
        </div>
      </section>
    `;
  }

  private buildStrategySection(): string {
    return `
      <section class="content-section" data-section="strategy">
        <div class="section-header">
          <span class="section-header-icon">💡</span>
          <div>
            <h2>战术技巧</h2>
            <p>决胜千里，运筹帷幄</p>
          </div>
        </div>

        <div class="info-card">
          <div class="info-card-header">
            <h3 class="info-card-title">开局策略</h3>
          </div>
          <div class="info-card-desc">
            1. <strong>发展经济</strong>：开局后立即建造2-3个农场，确保金币收入。<br>
            2. <strong>训练军队</strong>：建造兵营，训练一定数量的剑士保护领土。<br>
            3. <strong>侦察地形</strong>：派少量单位探索地图，了解敌方位置。<br>
            4. <strong>规划发展</strong>：根据地形和敌情制定发展方向。
          </div>
        </div>

        <div class="info-card">
          <div class="info-card-header">
            <h3 class="info-card-title">中期发展</h3>
          </div>
          <div class="info-card-desc">
            1. <strong>扩张领土</strong>：占领资源丰富的区域，建造更多经济建筑。<br>
            2. <strong>完善兵种</strong>：根据敌方兵种配置，训练克制单位。<br>
            3. <strong>研发科技</strong>：投资科技树提升整体实力。<br>
            4. <strong>外交斡旋</strong>：与远方国家结盟，孤立主要敌人。
          </div>
        </div>

        <div class="info-card">
          <div class="info-card-header">
            <h3 class="info-card-title">进攻技巧</h3>
          </div>
          <div class="info-card-desc">
            1. <strong>集中兵力</strong>：不要分散兵力，集中优势兵力各个击破。<br>
            2. <strong>利用克制</strong>：观察敌方兵种，派出克制单位。<br>
            3. <strong>骑兵迂回</strong>：用骑兵绕后袭击敌方弓箭手和道士。<br>
            4. <strong>步步为营</strong>：推进时建造前线建筑作为补给点。
          </div>
        </div>

        <div class="info-card">
          <div class="info-card-header">
            <h3 class="info-card-title">防守技巧</h3>
          </div>
          <div class="info-card-desc">
            1. <strong>城墙防线</strong>：在要道建造城墙阻挡敌军。<br>
            2. <strong>箭塔网络</strong>：在边境部署箭塔形成火力网。<br>
            3. <strong>地形优势</strong>：在山丘、森林等有利地形防守。<br>
            4. <strong>快速反应</strong>：保留机动部队应对突破。
          </div>
        </div>

        <div class="tip-box">
          <div class="tip-header">
            <span>💡</span>
            <span>高阶技巧</span>
          </div>
          <ul class="tip-list">
            <li>善用暂停功能（空格键）在关键时刻进行战术规划</li>
            <li>创建多个编队，分别控制不同战场的部队</li>
            <li>让盾兵在前，弓箭手在后形成阵型</li>
            <li>保持一定经济储备，以应对紧急情况</li>
            <li>观察敌方兵力空虚时发动突袭</li>
            <li>必要时可以拆除后方建筑回收部分资金</li>
          </ul>
        </div>
      </section>
    `;
  }

  private setupEvents(): void {
    // Close button
    const closeBtn = this.container.querySelector('.close-btn');
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.hide();
      });
    }

    // Click overlay to close
    const overlay = this.container.querySelector('.help-overlay');
    overlay?.addEventListener('click', () => this.hide());

    // ESC key to close (only when panel is visible, avoid conflict)
    this.handleKeyDown = this.handleKeyDown.bind(this);
    document.addEventListener('keydown', this.handleKeyDown);

    // Navigation
    const navItems = this.container.querySelectorAll('.nav-item');
    navItems.forEach(item => {
      item.addEventListener('click', () => {
        const section = (item as HTMLElement).dataset.section;
        if (section) {
          this.navigateToSection(section);
        }
      });
    });
  }

  private handleKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Escape' && this.visible) {
      e.preventDefault();
      e.stopPropagation();
      this.hide();
    }
  }

  private navigateToSection(sectionId: string): void {
    // Update nav
    const navItems = this.container.querySelectorAll('.nav-item');
    navItems.forEach(item => {
      item.classList.toggle('active', (item as HTMLElement).dataset.section === sectionId);
    });

    // Update content
    const sections = this.container.querySelectorAll('.content-section');
    sections.forEach(section => {
      section.classList.toggle('active', (section as HTMLElement).dataset.section === sectionId);
    });

    // Scroll to top
    const content = this.container.querySelector('.help-content');
    if (content) {
      content.scrollTop = 0;
    }

    this.currentSection = sectionId;
  }

  show(): void {
    this.visible = true;
    this.container.classList.add('visible');
  }

  hide(): void {
    this.visible = false;
    this.container.classList.remove('visible');
  }

  toggle(): void {
    if (this.visible) {
      this.hide();
    } else {
      this.show();
    }
  }

  isVisible(): boolean {
    return this.visible;
  }

  dispose(): void {
    document.removeEventListener('keydown', this.handleKeyDown);
    this.container.remove();
  }
}
